import { gsap } from "gsap";

export default class MenuManager {
  constructor() {
    this.menuToggle = document.getElementById("menuToggle");
    this.menuOverlay = document.getElementById("menuOverlay");
    this.menuCurtain = this.menuOverlay.querySelector(".menu-curtain");
    this.menuNav = this.menuOverlay.querySelector(".menu-nav");
    this.menuLinks = this.menuOverlay.querySelectorAll(".menu-link");
    this.menuOpen = false;
    this.isAnimating = false;
    this.isLabelTransitioning = false;

    // Initial label setup
    this.updateMenuToggleLabel("( M E N U )");

    this.menuToggle.addEventListener("click", () => {
      if (this.isAnimating) return;
      this.menuOpen ? this.closeMenu() : this.openMenu();
    });

    this.menuLinks.forEach((btn) => {
      btn.addEventListener("click", () => {
        const target = btn.getAttribute("data-target");
        this.closeMenu();
        setTimeout(() => {
          if (target === "contact") {
            // Scroll to the bottom of the page for contact
            window.scrollTo({
              top: document.body.scrollHeight,
              behavior: "smooth"
            });
          } else {
            document
              .getElementById(target)
              .scrollIntoView({ behavior: "smooth" });
          }
        }, 900);
      });
    });
  }

  createLetterSpans(label) {
    const frag = {
      original: document.createElement("span"),
      clone: document.createElement("span"),
    };
    frag.original.className = "menu-letters original";
    frag.clone.className = "menu-letters clone";
    [...label].forEach((char) => {
      const o = document.createElement("span");
      o.textContent = char === " " ? "\u00A0" : char;
      frag.original.appendChild(o);

      const c = document.createElement("span");
      c.textContent = char === " " ? "\u00A0" : char;
      frag.clone.appendChild(c);
    });
    return frag;
  }

  setHoverHandlers(original, clone) {
    this.menuToggle.onmouseenter = () => {
      if (this.isLabelTransitioning) return;
      this.animateMenuLetters(original, clone, "in");
    };
    this.menuToggle.onmouseleave = () => {
      if (this.isLabelTransitioning) return;
      this.animateMenuLetters(original, clone, "out");
    };
  }

  animateMenuLetters(original, clone, direction = "in") {
    const duration = 0.28;
    const stagger = 0.035;
    const ease = "cubic-bezier(0.77,0,0.175,1)";
    if (direction === "in") {
      gsap.to(original.children, {
        y: "-120%",
        opacity: 0,
        stagger,
        duration,
        ease,
      });
      gsap.to(clone.children, {
        y: "0%",
        opacity: 1,
        stagger,
        duration,
        ease,
      });
    } else {
      gsap.to(clone.children, {
        y: "120%",
        opacity: 0,
        stagger: { each: stagger, from: "end" },
        duration,
        ease,
      });
      gsap.to(original.children, {
        y: "0%",
        opacity: 1,
        stagger: { each: stagger, from: "end" },
        duration,
        ease,
      });
    }
  }

  animateLabelSwap(label) {
    this.isLabelTransitioning = true;
    const original = this.menuToggle.querySelector(".menu-letters.original");
    const clone = this.menuToggle.querySelector(".menu-letters.clone");

    const outTl = gsap.timeline({
      onComplete: () => {
        this.menuToggle.innerHTML = "";
        const { original: newOriginal, clone: newClone } =
          this.createLetterSpans(label);
        this.menuToggle.appendChild(newOriginal);
        this.menuToggle.appendChild(newClone);

        gsap.set(newClone.children, { y: "120%", opacity: 0 });
        gsap.set(newClone, { zIndex: 2 });
        gsap.set(newOriginal, { zIndex: 1 });

        this.setHoverHandlers(newOriginal, newClone);

        gsap.fromTo(
          newOriginal.children,
          { y: "-120%", opacity: 0 },
          {
            y: "0%",
            opacity: 1,
            stagger: 0.035,
            duration: 0.28,
            ease: "cubic-bezier(0.77,0,0.175,1)",
            onComplete: () => {
              this.isLabelTransitioning = false;
            },
          }
        );
      },
    });

    outTl.to(
      original.children,
      {
        y: "120%",
        opacity: 0,
        stagger: 0.025,
        duration: 0.18,
        ease: "cubic-bezier(0.77,0,0.175,1)",
      },
      0
    );
    outTl.to(
      clone.children,
      {
        y: "120%",
        opacity: 0,
        stagger: 0.025,
        duration: 0.18,
        ease: "cubic-bezier(0.77,0,0.175,1)",
      },
      0
    );
  }

  updateMenuToggleLabel(label, smooth = false) {
    if (!smooth || !this.menuToggle.firstChild) {
      this.menuToggle.innerHTML = "";
      const { original, clone } = this.createLetterSpans(label);
      this.menuToggle.appendChild(original);
      this.menuToggle.appendChild(clone);

      gsap.set(clone.children, { y: "120%", opacity: 0 });
      gsap.set(clone, { zIndex: 2 });
      gsap.set(original, { zIndex: 1 });

      this.setHoverHandlers(original, clone);
      return;
    }
    this.animateLabelSwap(label);
  }

  openMenu() {
    if (this.isAnimating) return;
    this.isAnimating = true;
    this.menuOverlay.classList.add("active");
    this.menuToggle.classList.add("active");
    this.updateMenuToggleLabel("( C L O S E )", true);

    // Set initial states for menu links
    gsap.set(this.menuLinks, {
      y: 60,
      opacity: 0
    });

    gsap.to(this.menuCurtain, {
      y: "0%",
      duration: 1.2,
      ease: "power2.inOut",
      onStart: () => {
        this.menuNav.style.opacity = 0;
      },
      onComplete: () => {
        // Show nav container first
        gsap.to(this.menuNav, {
          opacity: 1,
          duration: 0.4,
          onComplete: () => {
            // Clean, editorial staggered animation
            gsap.to(this.menuLinks, {
              y: 0,
              opacity: 1,
              duration: 0.8,
              stagger: {
                each: 0.12,
                from: "start"
              },
              ease: "power3.out"
            });

            this.isAnimating = false;
          }
        });
      },
    });
    this.menuOpen = true;
  }

  closeMenu() {
    if (this.isAnimating) return;
    this.isAnimating = true;

    // Clean exit animation
    gsap.to(this.menuLinks, {
      y: -40,
      opacity: 0,
      duration: 0.5,
      stagger: {
        each: 0.08,
        from: "end"
      },
      ease: "power2.in",
      onComplete: () => {
        gsap.to(this.menuNav, {
          opacity: 0,
          duration: 0.3,
          onComplete: () => {
            // Animate curtain out
            gsap.to(this.menuCurtain, {
              y: "-100%",
              duration: 1.2,
              ease: "power2.inOut",
              onStart: () => {
                this.updateMenuToggleLabel("( M E N U )", true);
                this.menuToggle.classList.remove("active");
              },
              onComplete: () => {
                this.menuOverlay.classList.remove("active");
                this.isAnimating = false;
              },
            });
          }
        });
      }
    });

    this.menuOpen = false;
  }
}
